/**
 * Conversation operations — list, threads, delete, share, title, messages, import.
 */

import type { ArbiClient, components } from '@arbidocs/client'
import { requireData } from '../errors.js'
import { authenticatedFetch, type AuthHeaders } from '../fetch.js'

type ConversationImportResponse = components['schemas']['ConversationImportResponse']

export type { ConversationImportResponse }

export async function listConversations(arbi: ArbiClient) {
  return requireData(await arbi.fetch.GET('/v1/conversation/list'), 'Failed to fetch conversations')
}

/**
 * Import a conversation export into the active workspace.
 *
 * Accepts a ChatGPT data export (`conversations.json`), an OpenClaw session log, or a
 * Claude Code transcript (`.jsonl`); `source='auto'` lets the server detect the format.
 * Uses raw multipart fetch (the typed client can't express file uploads), mirroring
 * `files.uploadFile`. The active workspace + cipher are resolved server-side from the
 * session, so no extra header is needed.
 */
export async function importConversations(
  auth: AuthHeaders,
  fileData: Blob,
  fileName: string,
  source = 'auto'
): Promise<ConversationImportResponse> {
  const formData = new FormData()
  formData.append('file', fileData, fileName)

  const res = await authenticatedFetch({
    ...auth,
    path: `/v1/conversation/import?source=${encodeURIComponent(source)}`,
    method: 'POST',
    body: formData,
  })

  return res.json() as Promise<ConversationImportResponse>
}

export async function getConversationThreads(arbi: ArbiClient, conversationId: string) {
  return requireData(
    await arbi.fetch.GET('/v1/conversation/{conversation_ext_id}/threads', {
      params: { path: { conversation_ext_id: conversationId } },
    }),
    'Failed to fetch threads'
  )
}

export async function deleteConversation(arbi: ArbiClient, conversationId: string) {
  return requireData(
    await arbi.fetch.DELETE('/v1/conversation/{conversation_ext_id}', {
      params: { path: { conversation_ext_id: conversationId } },
    }),
    'Failed to delete conversation'
  )
}

export async function shareConversation(arbi: ArbiClient, conversationId: string) {
  return requireData(
    await arbi.fetch.POST('/v1/conversation/{conversation_ext_id}/share', {
      params: { path: { conversation_ext_id: conversationId } },
    }),
    'Failed to share conversation'
  )
}

export async function updateConversationTitle(
  arbi: ArbiClient,
  conversationId: string,
  title: string
) {
  return requireData(
    await arbi.fetch.PATCH('/v1/conversation/{conversation_ext_id}/title', {
      params: { path: { conversation_ext_id: conversationId } },
      body: { title },
    }),
    'Failed to update title'
  )
}

export async function getMessage(arbi: ArbiClient, messageId: string) {
  return requireData(
    await arbi.fetch.GET('/v1/conversation/message/{message_ext_id}', {
      params: { path: { message_ext_id: messageId } },
    }),
    'Failed to fetch message'
  )
}

export async function deleteMessage(arbi: ArbiClient, messageId: string) {
  return requireData(
    await arbi.fetch.DELETE('/v1/conversation/message/{message_ext_id}', {
      params: { path: { message_ext_id: messageId } },
    }),
    'Failed to delete message'
  )
}
